import isEqual from 'lodash/isEqual';
import { useCallback, useRef, useSyncExternalStore } from 'react';
import { snapshotAtom, type SidePanelStateSnapshot } from '../state/atoms';
import { useInternalSidePanelSystem } from './useInternalSidePanelSystem';

export type { SidePanelStateSnapshot } from '../state/atoms';

const selectSnapshot = (state: SidePanelStateSnapshot) => state;

/**
 * Subscribes to side-panel session, layout, surface, and contribution state and selects a snapshot value.
 *
 * @typeParam TValue - Value returned by the selector.
 * @param selector - Projection of the current snapshot; defaults to returning the entire snapshot.
 * @returns The selected value. Closed sessions expose null for subject and visiblePanelId.
 * @remarks
 * Requires SidePanelSystemProvider. Structurally equal selected values retain their identity.
 * The isOpen query compares a supplied subject by reference; isDeclared checks the surface and all contributions.
 */
export function useSidePanelState<TValue = SidePanelStateSnapshot>(
    selector: (state: SidePanelStateSnapshot) => TValue = selectSnapshot as (state: SidePanelStateSnapshot) => TValue
): TValue {
    const { store } = useInternalSidePanelSystem();
    const selectedValueRef = useRef<{ value: TValue } | null>(null);
    const subscribe = useCallback((onStoreChange: () => void) => store.sub(snapshotAtom, onStoreChange), [store]);
    const getSnapshot = useCallback(() => {
        const value = selector(store.get(snapshotAtom));
        if (selectedValueRef.current && isEqual(selectedValueRef.current.value, value)) {
            return selectedValueRef.current.value;
        }
        selectedValueRef.current = { value };
        return value;
    }, [selector, store]);

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
